// Compact, model-readable accounts of what a flow returned.
//
// These are what the post-result debrief reads, and what the chat model sees in
// its history instead of "[Found 14 contacts]". They never carry the rows: the
// user is looking at those on screen, the model only needs the shape of them.
// Kept small on purpose (a few hundred tokens) because one is sent on every run
// and stored on every result message.
//
// One builder per flow `kind`, matching backend/services/searchDebrief.js:
//   search | person   summarizeSearchOutcome
//   batch            summarizeBatchOutcome
//   lookalike        summarizeLookalikeOutcome
//   enrich           summarizeEnrichOutcome
//   verify           summarizeVerifyOutcome
//   research         summarizeResearchOutcome
//   company          summarizeCompanyResearch
// The search and lookalike shapes are exactly what the agent loop's search_contacts
// and find_similar_companies tools return to the model (VerifierPage resumeAgentTurn);
// the rest go to the separate debrief call. One builder per flow, whichever reads it.

const TOP_N = 5;

const SENIORITY_RULES = [
  ['c_level',  /\b(chief|c[a-z]{1,2}o|ceo|cfo|cto|coo|cmo|cro|ciso|cio|founder|co-?founder|president|owner|partner)\b/i],
  ['vp',       /\b(vp|vice president|svp|evp|avp)\b/i],
  ['director', /\b(director|head of)\b/i],
  ['manager',  /\b(manager|lead|supervisor)\b/i],
];

function seniorityOf(title) {
  const t = String(title || '');
  for (const [bucket, re] of SENIORITY_RULES) if (re.test(t)) return bucket;
  return 'other';
}

function topCounts(values, n = TOP_N) {
  const counts = new Map();
  for (const v of values) {
    const key = String(v || '').trim();
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, n);
}

function splitList(s) {
  return String(s || '').split(',').map(x => x.trim()).filter(Boolean);
}

function firstNames(list, n = TOP_N) {
  return (Array.isArray(list) ? list : []).map(x => String(x || '').trim()).filter(Boolean).slice(0, n);
}

// ── search / person ───────────────────────────────────────────────────────────

/**
 * @param {object} args
 * @param {object}  args.criteria         the parsed search criteria
 * @param {Array}   args.contacts         [{ contact, result }] pairs as delivered
 * @param {object}  [args.shortfall]      the backend's shortfall object, if any
 * @param {number}  [args.chargedCount]   lead credits actually debited
 * @param {object}  [args.credits]        { remaining, allowance } after the search
 * @param {string}  [args.source]         provenance string from the backend
 * @param {Array}   [args.staleDropped]
 * @param {number}  [args.crmSkipped]
 * @param {number}  [args.creditCappedFrom]  what was asked for before the balance clamped it
 */
export function summarizeSearchOutcome({
  criteria = {}, contacts = [], shortfall = null, chargedCount = null, credits = null,
  source = '', staleDropped = [], crmSkipped = 0, creditCappedFrom = null,
} = {}) {
  const rows = Array.isArray(contacts) ? contacts : [];
  const verdict = r => r?.result?.verdict || 'unknown';
  const count = v => rows.filter(r => verdict(r) === v).length;

  const seniority = { c_level: 0, vp: 0, director: 0, manager: 0, other: 0 };
  for (const r of rows) seniority[seniorityOf(r?.contact?.title)] += 1;

  const sf = shortfall || {};
  const requested = sf.requested ?? (parseInt(criteria.limit, 10) || rows.length);

  return {
    requested,
    requestedStated: !!(sf.requestedStated ?? criteria.limitStated),
    delivered:    rows.length,
    verified:     count('valid'),
    risky:        count('risky'),
    invalid:      rows.filter(r => !['valid', 'risky', 'linkedin-only', 'no-email'].includes(verdict(r))).length,
    linkedinOnly: count('linkedin-only'),
    noEmail:      count('no-email'),
    criteria: {
      titles:        splitList(criteria.titleKeyword).slice(0, 3),
      functionScope: criteria.functionScope || '',
      company:       criteria.companyName || criteria.companyDomain || '',
      states:        splitList(criteria.stateArray),
      country:       criteria.countryCode || '',
      industry:      criteria.industryOverride || '',
      employeeRange: criteria.employeeRange || '',
      revenueRange:  criteria.revenueRange || '',
      seniority:     criteria.filterLevel || '',
      // "Startups in Austin": the stage and the metro, so the take can say whether they held.
      stage:         criteria.companyStage || sf.stageAsked || '',
      city:          criteria.city || sf.cityAsked || '',
    },
    titleDistribution:   topCounts(rows.map(r => r?.contact?.title)),
    companyDistribution: topCounts(rows.map(r => r?.contact?.company)),
    seniority,
    gaps: {
      matched:              sf.matched ?? null,
      alreadyShown:         sf.alreadyShown || 0,
      cappedPerCompany:     sf.cappedPerCompany || 0,
      capValue:             sf.capValue ?? null,
      industrySplitTrimmed: sf.industrySplitTrimmed || 0,
      notCurrentlyEmployed: sf.notCurrentlyEmployed || 0,
      sourcesExhausted:     !!sf.sourcesExhausted,
      budgetExhausted:      !!sf.budgetExhausted,
      creditCappedFrom:     creditCappedFrom ?? null,
      // Named-company searches only: why the list is short in one word (unresolved_company,
      // none_at_company, title_mismatch), how many rows were thrown out for being at another
      // company, and what the company's people are actually called, so the take can offer them.
      reason:               sf.reason || null,
      offCompany:           sf.offCompany || 0,
      titlesAtCompany:      Array.isArray(sf.titlesAtCompany) ? sf.titlesAtCompany.slice(0, 6) : [],
      // Who those people are, by name and title, seen on the free look and not pulled. The
      // LinkedIn URL stays off the prompt payload; the chat keeps it on the message instead.
      peopleAtCompany:      Array.isArray(sf.peopleAtCompany)
        ? sf.peopleAtCompany.slice(0, 8).map(p => ({ name: p?.name || '', title: p?.title || '' }))
        : [],
      peopleAtCompanyTotal: sf.peopleAtCompanyTotal || 0,
      // "People located in" asks only: rows placed elsewhere, rows nobody could place, and the
      // place that was asked for, so the take can say what was withheld and why.
      locationMismatch:     sf.locationMismatch || 0,
      locationUnknown:      sf.locationUnknown || 0,
      locationAsked:        sf.locationAsked || '',
      // Stage and city asks: companies dropped for being too large or too old, and companies
      // whose headquarters turned out to be outside the metro.
      removedByStage:       sf.removedByStage || 0,
      removedByCity:        sf.removedByCity || 0,
      removedByIndustry:    sf.removedByIndustry || 0,
      // What the industry gate was judging against, how many people hold the titles before any
      // gate, and which discovered operators were walked directly - so an empty list can say what
      // was tried rather than "broaden".
      industryAsked:         Array.isArray(sf.industryAsked) ? sf.industryAsked.slice(0, 4) : [],
      totalMatchedTitles:    sf.totalMatchedTitles || 0,
      companiesChecked:      Array.isArray(sf.companiesChecked) ? sf.companiesChecked.slice(0, 5) : [],
      companiesCheckedTotal: sf.companiesCheckedTotal || 0,
      softDeadlineHit:       !!sf.softDeadlineHit,
      // "People located in <city>": what the LinkedIn people search found by where they live, and
      // where they fell away (wrong title now, employer outside the industry, no domain, already
      // yours). `exhausted` is the only thing that may be called "nobody left".
      resident: sf.residentSearch ? {
        found:        sf.residentSearch.found || 0,
        checked:      sf.residentSearch.checked || 0,
        offTitle:     sf.residentSearch.offTitle || 0,
        offIndustry:  sf.residentSearch.offIndustry || 0,
        noDomain:     sf.residentSearch.noDomain || 0,
        alreadyShown: sf.residentSearch.alreadyShown || 0,
        companies:    sf.residentSearch.companies || 0,
        delivered:    sf.residentSearch.delivered || 0,
        exhausted:    !!sf.residentSearch.exhausted,
        city:         sf.residentSearch.city || '',
      } : null,
    },
    linkedinOnlyFilled:    (sf.linkedinOnly || 0) > 0,
    linkedinOnlyAvailable: sf.linkedinOnlyAvailable || 0,
    creditsSpent:     typeof chargedCount === 'number' ? chargedCount : null,
    creditsRemaining: typeof credits?.remaining === 'number' ? credits.remaining : null,
    source:           String(source || ''),
    staleDropped:     Array.isArray(staleDropped) ? staleDropped.length : 0,
    crmSkipped:       crmSkipped || 0,
  };
}

// ── batch ─────────────────────────────────────────────────────────────────────

/**
 * A batch run over a company list (CSV batch, direct batch, finish-remaining,
 * named-contacts CSV). `data` is the backend's batch result object.
 */
export function summarizeBatchOutcome(data = {}, { titles = [], countPerCompany = null, fileName = '' } = {}) {
  const results = Array.isArray(data?.results) ? data.results : [];
  const byStatus = (s) => results.filter(r => r?.status === s);
  const companyOf = r => r?.company || r?.name || r?.contact?.company || '';
  const foundRows = byStatus('found');
  const verdict = r => r?.result?.verdict || '';

  const companiesWithResults = new Set(foundRows.map(companyOf).filter(Boolean));
  const companiesNone = [...new Set(results.filter(r => r?.status !== 'found' && r?.status !== 'skipped').map(companyOf).filter(Boolean))];

  return {
    fileName:           String(fileName || ''),
    titles:             firstNames(titles, 4),
    countPerCompany:    countPerCompany ?? null,
    total:              data?.total ?? results.length,
    companiesProcessed: data?.companiesProcessed ?? data?.total ?? results.length,
    companiesFound:     data?.companiesFound ?? companiesWithResults.size,
    companiesNone:      firstNames(companiesNone),
    companiesNoneCount: companiesNone.length,
    contactsFound:      data?.contactsFound ?? data?.found ?? foundRows.length,
    verified:           data?.verifiedFound ?? foundRows.filter(r => verdict(r) === 'valid').length,
    risky:              foundRows.filter(r => verdict(r) === 'risky').length,
    linkedinOnly:       data?.linkedinOnlyFound ?? foundRows.filter(r => verdict(r) === 'linkedin-only').length,
    notFound:           data?.notFound ?? byStatus('not_found').length,
    errors:             byStatus('error').length,
    partial:            !!data?.partial,
    partialReason:      data?.partialReason || '',
    creditsRemaining:   typeof data?.credits?.remaining === 'number' ? data.credits.remaining : null,
  };
}

// ── lookalike ─────────────────────────────────────────────────────────────────

export function summarizeLookalikeOutcome({ seed = {}, results = [], requested = null, delivered = null, shortfall = '', basis = '' } = {}) {
  const rows = Array.isArray(results) ? results : [];
  const conf = (c) => String(c || '').toLowerCase();
  return {
    seed: {
      name:          seed?.name || '',
      domain:        seed?.domain || '',
      industry:      seed?.industry || '',
      deliveryModel: seed?.deliveryModel || '',
      employees:     seed?.employees || '',
    },
    basis:      String(basis || ''),
    requested:  requested ?? rows.length,
    delivered:  delivered ?? rows.length,
    topNames:   rows.slice(0, TOP_N).map(r => ({ name: r?.name || '', industry: r?.industry || '', confidence: r?.confidence || '' })),
    industries: topCounts(rows.map(r => r?.industry)),
    confidence: {
      high:   rows.filter(r => conf(r?.confidence) === 'high').length,
      medium: rows.filter(r => conf(r?.confidence) === 'medium').length,
      low:    rows.filter(r => conf(r?.confidence) === 'low').length,
    },
    weakest:   rows.filter(r => conf(r?.confidence) === 'low').slice(0, 3).map(r => r?.name || '').filter(Boolean),
    shortfall: String(shortfall || ''),
  };
}

// ── companies by category ─────────────────────────────────────────────────────

/**
 * A company list found by industry, place and stage. Counts and the first few names only; the
 * rows are on screen, and the whole thing has to fit the tool-result cap.
 */
export function summarizeCompanyListOutcome({ criteria = {}, results = [], requested = null, delivered = null, shortfall = '', rejected = {} } = {}) {
  const rows = Array.isArray(results) ? results : [];
  const conf = (c) => String(c || '').toLowerCase();
  return {
    criteria: {
      industry: criteria?.industry || '',
      location: criteria?.city || (Array.isArray(criteria?.states) ? criteria.states.join(', ') : (criteria?.location || '')),
      stage:    criteria?.stage || '',
      extra:    criteria?.extra || '',
    },
    requested: requested ?? rows.length,
    delivered: delivered ?? rows.length,
    topNames:  rows.slice(0, TOP_N).map(r => ({
      name: r?.name || '', hqCity: r?.hqCity || '', employees: r?.employees || '', foundedYear: r?.foundedYear || '', confidence: r?.confidence || '',
    })),
    confidence: {
      high:   rows.filter(r => conf(r?.confidence) === 'high').length,
      medium: rows.filter(r => conf(r?.confidence) === 'medium').length,
      low:    rows.filter(r => conf(r?.confidence) === 'low').length,
    },
    weakest:   rows.filter(r => conf(r?.confidence) === 'low').slice(0, 3).map(r => r?.name || '').filter(Boolean),
    rejected: {
      industry:   rejected?.industry || 0,
      stage:      rejected?.stage || 0,
      city:       rejected?.city || 0,
      unverified: rejected?.unverified || 0,
    },
    creditsSpent: 0,
    shortfall: String(shortfall || ''),
  };
}

// ── enrich ────────────────────────────────────────────────────────────────────

export function summarizeEnrichOutcome({ summary = {}, results = [], selectedFields = [], fileName = '' } = {}) {
  const rows = Array.isArray(results) ? results : [];
  const fields = Array.isArray(selectedFields) ? selectedFields : [];
  const nameOf = r => r?.matchedAs || r?.inputName || r?.company || '';
  const fillRate = {};
  for (const f of fields) {
    const filled = rows.filter(r => r && r[f] !== undefined && r[f] !== null && String(r[f]).trim() !== '').length;
    fillRate[f] = rows.length ? Math.round((filled / rows.length) * 100) : 0;
  }
  const statusIs = (s) => rows.filter(r => String(r?.status || '').toLowerCase() === s);
  return {
    fileName:   String(fileName || ''),
    total:      rows.length,
    enriched:   summary?.enriched ?? statusIs('enriched').length,
    partial:    summary?.partial  ?? statusIs('partial').length,
    notFound:   summary?.notFound ?? statusIs('not found').length,
    fields,
    fillRate,
    notFoundNames: firstNames(statusIs('not found').map(nameOf)),
    partialNames:  firstNames(statusIs('partial').map(nameOf), 3),
    industries: topCounts(rows.map(r => r?.industry)),
    locations:  topCounts(rows.map(r => r?.hqLocation)),
    sizes:      topCounts(rows.map(r => r?.employees)),
  };
}

// ── verify ────────────────────────────────────────────────────────────────────

export function summarizeVerifyOutcome({ rows = [], tally = {}, source = 'typed', requested = null } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const domains = topCounts(list.map(r => String(r?.email || '').split('@')[1]));
  const checked = list.length;
  return {
    source:    String(source || 'typed'),
    requested: requested ?? checked,
    checked,
    valid:     tally?.valid   || 0,
    risky:     tally?.risky   || 0,
    invalid:   tally?.invalid || 0,
    skipped:   tally?.skipped || 0,
    validShare: checked ? Math.round(((tally?.valid || 0) / checked) * 100) : 0,
    domains,
    reasons:   topCounts(list.map(r => r?.detail || r?.smtpNote), 3),
  };
}

// ── research ──────────────────────────────────────────────────────────────────

export function summarizeResearchOutcome({ researchData = {}, contact = null } = {}) {
  const rd = researchData || {};
  return {
    subject:         rd.subject || contact?.name || '',
    company:         rd.freshCompany || contact?.company || rd.originalCompany || '',
    title:           contact?.title || '',
    who:             String(rd.who || '').slice(0, 300),
    angles:          (rd.angles || []).slice(0, 3).map(a => String(a).slice(0, 160)),
    painPoints:      (rd.painPoints || []).slice(0, 3).map(p => String(p).slice(0, 160)),
    hasDraft:        !!rd.outreachDraft,
    companyMismatch: !!rd.companyMismatch,
    originalCompany: rd.originalCompany || '',
    freshCompany:    rd.freshCompany || '',
    ...(rd.companySnapshot ? {
      companyDoes:      String(rd.companySnapshot.whatTheyDo || '').slice(0, 200),
      companyFit:       rd.companySnapshot.fit || 'unknown',
      companyFitReason: String(rd.companySnapshot.fitReason || '').slice(0, 200),
    } : {}),
  };
}

// ── company research ──────────────────────────────────────────────────────────
// Also the research_company tool's result in the agent loop, so the model answers from exactly
// what the Company Brief card shows.

export function summarizeCompanyResearch(data = {}, { researchedPerson = '' } = {}) {
  const d = data || {};
  if (d.notFound) return { name: d.name || '', domain: d.domain || '', notFound: true };
  return {
    name:         d.name || '',
    domain:       d.domain || '',
    whatTheyDo:   String(d.whatTheyDo || '').slice(0, 400),
    whoTheySell:  String(d.whoTheySell || '').slice(0, 160),
    industry:     d.industry || '',
    employees:    d.employees || '',
    revenue:      d.revenue || '',
    hqLocation:   d.hqLocation || '',
    foundedYear:  d.foundedYear || '',
    fundingStage: d.fundingStage || '',
    recentNews:   String(d.recentNews || '').slice(0, 200),
    signals:      (d.signals || []).slice(0, 3).map(s => String(s).slice(0, 160)),
    fit:          d.fit || 'unknown',
    fitReason:    String(d.fitReason || '').slice(0, 200),
    ...(researchedPerson ? { researchedPerson } : {}),
  };
}

// ── history line ──────────────────────────────────────────────────────────────

/**
 * One line for the model's conversation history: what this run asked for and
 * what it got, compact enough to sit in every turn's context.
 */
export function describeSearchOutcome(summary, kind = 'search') {
  if (!summary) return '';
  switch (kind) {
    case 'batch': {
      const t = summary.titles?.length ? ` for ${summary.titles.join('/')}` : '';
      return `[Batch${summary.fileName ? ` (${summary.fileName})` : ''}${t}: ${summary.companiesFound} of ${summary.total} companies had results, ` +
             `${summary.contactsFound} contacts (${summary.verified} verified${summary.linkedinOnly ? `, ${summary.linkedinOnly} LinkedIn-only` : ''})` +
             (summary.companiesNone?.length ? `; none at: ${summary.companiesNone.join(', ')}` : '') +
             (summary.partial ? `; partial (${summary.partialReason || 'stopped early'})` : '') + ']';
    }
    case 'lookalike':
      return `[Look-alikes of ${summary.seed?.name || 'seed'}: ${summary.delivered} of ${summary.requested}` +
             (summary.topNames?.length ? `; top: ${summary.topNames.slice(0, 3).map(n => n.name).join(', ')}` : '') +
             (summary.shortfall ? `; short because ${summary.shortfall}` : '') + ']';
    case 'companies': {
      const c = summary.criteria || {};
      const what = [c.stage ? `${c.stage}s` : 'companies', c.industry ? `in ${c.industry}` : '', c.location ? `in ${c.location}` : ''].filter(Boolean).join(' ');
      return `[Company list: ${summary.delivered} of ${summary.requested} ${what}` +
             (summary.topNames?.length ? `; top: ${summary.topNames.slice(0, 3).map(n => n.name).join(', ')}` : '') +
             (summary.shortfall ? `; short because ${summary.shortfall}` : '') + ']';
    }
    case 'enrich':
      return `[Enriched ${summary.fileName ? `${summary.fileName}: ` : ''}${summary.enriched} of ${summary.total} companies, ${summary.partial} partial, ${summary.notFound} not found` +
             (summary.notFoundNames?.length ? `; not found: ${summary.notFoundNames.join(', ')}` : '') + ']';
    case 'verify':
      return `[Verified ${summary.checked} emails: ${summary.valid} valid, ${summary.risky} risky, ${summary.invalid} invalid` +
             (summary.skipped ? `, ${summary.skipped} skipped` : '') + ']';
    case 'research':
      return `[Research on ${summary.subject}${summary.company ? ` at ${summary.company}` : ''}` +
             (summary.angles?.length ? `; angles: ${summary.angles.join(' | ')}` : '') +
             (summary.companyMismatch ? `; company mismatch (${summary.originalCompany} -> ${summary.freshCompany})` : '') +
             (summary.companyDoes ? `; their company: ${summary.companyDoes} (fit: ${summary.companyFit || 'unknown'})` : '') + ']';
    case 'company':
      return summary.notFound
        ? `[Company research on ${summary.name || summary.domain}: nothing found on what it does]`
        : `[Company research on ${summary.name}${summary.domain ? ` (${summary.domain})` : ''}: ${summary.whatTheyDo || 'no description'}` +
          `; fit: ${summary.fit}${summary.fitReason ? ` (${summary.fitReason})` : ''}]`;
    case 'person':
      return `[Person lookup: ${summary.personName || ''}${summary.personAt ? ` at ${summary.personAt}` : ''}; ` +
             `${summary.delivered} match${summary.delivered === 1 ? '' : 'es'}, ${summary.verified} verified]`;
    default: {
      const c = summary.criteria || {};
      const what = [
        c.titles?.length ? c.titles.join('/') : (c.functionScope || 'contacts'),
        c.company ? `at ${c.company}` : '',
        c.stage ? `at ${c.stage === 'smb' ? 'small companies' : c.stage === 'midmarket' ? 'mid-market companies' : `${c.stage}s`}` : '',
        c.industry ? `in ${c.industry}` : '',
        c.city ? `in ${c.city}` : (c.states?.length ? `in ${c.states.join(', ')}` : ''),
      ].filter(Boolean).join(' ');
      const mix = [
        `${summary.verified} verified`,
        summary.risky ? `${summary.risky} risky` : '',
        summary.linkedinOnly ? `${summary.linkedinOnly} LinkedIn-only` : '',
      ].filter(Boolean).join(', ');
      const companies = (summary.companyDistribution || []).slice(0, 3).map(([n, k]) => `${n} x${k}`).join(', ');
      const credits = summary.creditsSpent != null ? `; ${summary.creditsSpent} credits spent` : '';
      const g = summary.gaps || {};
      // Who else was seen at a named company, by name, whatever the reason the list was short.
      // This used to surface titles only, and only when nothing at all was delivered, so one
      // delivered row was enough to make the rest of the company vanish from the model's memory.
      const seen = (g.peopleAtCompany || []).filter(p => p?.name).slice(0, 5);
      const also = seen.length
        ? `; also at ${c.company || 'that company'}, not pulled: ${seen.map(p => p.title ? `${p.name} (${p.title})` : p.name).join(', ')}` +
          ((g.peopleAtCompanyTotal || 0) > seen.length ? ` and ${g.peopleAtCompanyTotal - seen.length} more` : '')
        : '';
      const why = g.reason === 'unresolved_company' ? '; company could not be identified, nothing searched'
        : g.reason === 'title_mismatch' ? `; nobody there had that title` +
            (seen.length ? '' : `, titles seen: ${(g.titlesAtCompany || []).slice(0, 3).map(t => t.title).join(', ')}`)
        : g.reason === 'none_at_company' ? '; nobody found at that company'
        : '';
      const place = (g.locationMismatch || g.locationUnknown)
        ? `; people located in ${g.locationAsked || 'the requested place'} only: ${g.locationMismatch || 0} elsewhere and ${g.locationUnknown || 0} unplaced were withheld`
        : '';
      return `[Search: ${what}; asked ${summary.requested}, delivered ${summary.delivered} (${mix})` +
             (companies ? `; companies: ${companies}` : '') + credits + why + also + place + ']';
    }
  }
}
